import PropTypes from 'prop-types';
import { PiTrashBold } from 'react-icons/pi';

// TODO: 暫關待優化 — 自動 scrollIntoView（還原時一併還原 import）
// import { useEffect, useRef } from 'react';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import { Input } from '#/modules/commonComponents/form/Input';
import { getNestedError } from '#/modules/commonComponents/form/formFieldUtils';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';

const LINE_GRID_COLS =
  'grid-cols-[2rem_7.5rem_minmax(8rem,1fr)_minmax(9rem,12rem)_5rem_7rem_2.5rem]';

const LINE_MIN_WIDTH = 'min-w-[54rem]';

const LINE_HEADER_CLASS = cn(
  'sticky top-0 z-[var(--z-content-sticky)] grid w-full gap-3',
  'border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  LINE_GRID_COLS,
);

const LINE_ROW_CLASS = cn(
  'grid w-full items-start gap-3 px-3 py-3',
  LINE_GRID_COLS,
);

function resolveLineInvStockId(line) {
  return line?.invStockId || line?.inventoryStockId || '';
}

function formatWarehouseLocation(line) {
  return [line.warehouseLabel, line.locationLabel].filter(Boolean).join(' / ') || '—';
}

function qtyErrorMessage(reason) {
  if (reason === 'negative') {
    return '不可小於 0';
  }
  if (reason === 'blank') {
    return '不可空白';
  }
  return '';
}

const ScanLineRow = ({
  index,
  line,
  onCountedQtyChange,
  onCountedQtyBlur,
  onRemove,
  qtyErrorReason = null,
  isHighlighted = false,
}) => {
  const warehouseLocation = formatWarehouseLocation(line);
  const fieldId = `stocktake-lines.${index}.countedQty`;
  const errors = qtyErrorReason
    ? { [fieldId]: { message: qtyErrorMessage(qtyErrorReason) } }
    : undefined;
  const fieldError = getNestedError(errors, fieldId);
  const invStockId = resolveLineInvStockId(line);

  return (
    <li
      data-inv-stock-id={invStockId || undefined}
      className={cn(
        LINE_ROW_CLASS,
        qtyErrorReason && 'bg-red-50 ring-1 ring-red-200',
        !qtyErrorReason && isHighlighted && 'bg-green-50 ring-1 ring-green-300',
      )}
    >
      <p className="pt-2 text-sm tabular-nums text-gray-600">
        {`#${index + 1}`}
      </p>
      <p className="min-w-0 pt-2 font-mono text-sm text-gray-800">
        {line.stockNo || '—'}
      </p>
      <div className="min-w-0 pt-1">
        <MaterialNameStack name={line.mtrName} code={line.mtrNo} />
      </div>
      <p
        className="min-w-0 truncate pt-2 text-sm text-gray-800"
        title={warehouseLocation}
      >
        {warehouseLocation}
      </p>
      <p className="pt-2 text-sm tabular-nums text-gray-800">
        {line.systemQty == null || line.systemQty === '' ? '—' : line.systemQty}
      </p>
      <div className="min-w-0">
        <Input
          id={fieldId}
          type="number"
          step="any"
          value={line.countedQty === '' || line.countedQty == null
            ? ''
            : String(line.countedQty)}
          aria-invalid={fieldError ? true : undefined}
          onChange={(event) => onCountedQtyChange(index, event.target.value)}
          onBlur={(event) => onCountedQtyBlur(index, event.target.value)}
          className="w-full"
        />
        {fieldError ? (
          <p className="form_error">{fieldError.message}</p>
        ) : null}
      </div>
      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
          aria-label={`移除 ${line.stockNo || '明細'}`}
          onClick={() => onRemove(index)}
        >
          <PiTrashBold className="size-4" aria-hidden />
        </Button>
      </div>
    </li>
  );
};

ScanLineRow.propTypes = {
  index: PropTypes.number.isRequired,
  line: PropTypes.shape({
    invStockId: PropTypes.string,
    inventoryStockId: PropTypes.string,
    stockNo: PropTypes.string,
    mtrNo: PropTypes.string,
    mtrName: PropTypes.string,
    warehouseLabel: PropTypes.string,
    locationLabel: PropTypes.string,
    systemQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    countedQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  onCountedQtyChange: PropTypes.func.isRequired,
  onCountedQtyBlur: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  qtyErrorReason: PropTypes.oneOf(['blank', 'negative']),
  isHighlighted: PropTypes.bool,
};

const StocktakeScanLinesEditor = ({
  lines = [],
  onCountedQtyChange,
  onCountedQtyBlur,
  onRemove,
  invalidByIndex = {},
  highlightInvStockId = null,
}) => {
  // TODO: 暫關待優化 — 掃碼成功後自動滾到高亮列；列綠高亮／data-inv-stock-id 仍啟用
  // const listContainerRef = useRef(null);
  //
  // useEffect(() => {
  //   if (!highlightInvStockId || !listContainerRef.current) {
  //     return;
  //   }
  //   const selector = `[data-inv-stock-id="${CSS.escape(String(highlightInvStockId))}"]`;
  //   const rowEl = listContainerRef.current.querySelector(selector);
  //   rowEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  // }, [highlightInvStockId, lines]);

  if (lines.length === 0) {
    return (
      <p className="form-section__empty-state">
        尚未加入庫存。請掃描或輸入庫存編號後按 Enter。
      </p>
    );
  }

  return (
    <div
      // ref={listContainerRef}
      className={cn(
        'overflow-x-auto rounded-md border border-gray-200',
        'max-h-[min(32rem,calc(100vh-14rem))] overflow-y-auto',
      )}
    >
      <div className={cn('w-full', LINE_MIN_WIDTH)}>
        <div className={LINE_HEADER_CLASS}>
          <span>#</span>
          <span>庫存編號</span>
          <span>物料/料號</span>
          <span>倉別/儲位</span>
          <span>系統量</span>
          <span>盤點量</span>
          <span className="sr-only">操作</span>
        </div>
        <ul className="m-0 list-none divide-y divide-slate-100 p-0">
          {lines.map((line, index) => {
            const invStockId = resolveLineInvStockId(line);
            return (
              <ScanLineRow
                key={invStockId || `${line.stockNo}-${index}`}
                index={index}
                line={line}
                onCountedQtyChange={onCountedQtyChange}
                onCountedQtyBlur={onCountedQtyBlur}
                onRemove={onRemove}
                qtyErrorReason={invalidByIndex[index] || null}
                isHighlighted={Boolean(
                  highlightInvStockId
                  && invStockId
                  && highlightInvStockId === invStockId,
                )}
              />
            );
          })}
        </ul>
      </div>
    </div>
  );
};

StocktakeScanLinesEditor.propTypes = {
  lines: PropTypes.arrayOf(PropTypes.shape({
    invStockId: PropTypes.string,
    inventoryStockId: PropTypes.string,
    stockNo: PropTypes.string,
    mtrNo: PropTypes.string,
    mtrName: PropTypes.string,
    warehouseLabel: PropTypes.string,
    locationLabel: PropTypes.string,
    systemQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    countedQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })),
  onCountedQtyChange: PropTypes.func.isRequired,
  onCountedQtyBlur: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  invalidByIndex: PropTypes.objectOf(
    PropTypes.oneOf(['blank', 'negative']),
  ),
  highlightInvStockId: PropTypes.string,
};

export default StocktakeScanLinesEditor;
